import React, { useState, useEffect } from 'react';
import { supabase, PRODUCTS_TABLE, handleSupabaseError } from '../lib/supabase';
import { AlertTriangle, Info, RefreshCw, HelpCircle, WifiOff, ClipboardList, ExternalLink, MailX, Trash2, ShoppingCart, Copy, Filter, CheckCircle, Clock, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import { getOrderLogs, clearOrderLogs, fetchOrderStatuses, importWooOrders, sendCartEmail, getWooApiUrl, WooOrderLog } from '../lib/woocommerce';

export function DatabaseViewer() {
  const [activeTab, setActiveTab] = useState<'diagnostic' | 'log'>('diagnostic');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'failed'>('connecting');
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [orderLogs, setOrderLogs] = useState<WooOrderLog[]>([]);
  const [logFilter, setLogFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [dbInfo, setDbInfo] = useState<{ url: string, table: string }>({
    url: localStorage.getItem('supabase_url') || import.meta.env.VITE_SUPABASE_URL || 'Non définie',
    table: PRODUCTS_TABLE || 'Non définie'
  });

  const testConnection = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setErrorDetails(null);
      setConnectionStatus('connecting');

      // Test simple de connexion
      const { data, error } = await supabase
        .from(PRODUCTS_TABLE)
        .select('id')
        .limit(1);

      if (error) {
        console.error('Erreur de connexion:', error);
        setConnectionStatus('failed');
        setError(handleSupabaseError(error));
        setErrorDetails(error);
        return false;
      }

      setConnectionStatus('connected');
      return true;
    } catch (error: any) {
      console.error('Exception:', error);
      setConnectionStatus('failed');
      setError(error.message || 'Erreur inattendue lors de la connexion');
      setErrorDetails(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setErrorDetails(null);

      // Mettre à jour les informations de la base de données
      setDbInfo({
        url: localStorage.getItem('supabase_url') || import.meta.env.VITE_SUPABASE_URL || 'Non définie',
        table: PRODUCTS_TABLE || 'Non définie'
      });

      // Vérifier d'abord la connexion
      const isConnected = await testConnection();
      if (!isConnected) {
        return;
      }

      // Charger les données
      const { data, error } = await supabase
        .from(PRODUCTS_TABLE)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        throw error;
      }

      setData(data || []);
    } catch (error: any) {
      console.error('Erreur lors du chargement des données:', error);
      setError(handleSupabaseError(error));
      setErrorDetails(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'log') {
      refreshLogs();
    }
  }, [activeTab]);

  const refreshLogs = async () => {
    setIsLoadingLogs(true);
    try {
      await fetchOrderStatuses();
    } catch (e) {
      console.warn('Erreur fetch statuts:', e);
    }
    const logs = await getOrderLogs();
    setOrderLogs(logs);
    setIsLoadingLogs(false);
  };

  const handleClearLogs = async () => {
    await clearOrderLogs();
    setOrderLogs([]);
    toast.success('Logs effacés');
  };

  const handleImportOrders = async () => {
    setIsLoadingLogs(true);
    toast.loading('Import des commandes WooCommerce...', { id: 'import' });
    try {
      const count = await importWooOrders();
      toast.success(`${count} commande(s) importée(s) !`, { id: 'import', duration: 4000 });
      await refreshLogs();
    } catch (e: any) {
      toast.error(`Erreur import: ${e.message}`, { id: 'import' });
    }
    setIsLoadingLogs(false);
  };

  const isPaid = (log: WooOrderLog) => {
    return log.orderStatus === 'processing' || log.orderStatus === 'completed';
  };

  const filteredLogs = orderLogs.filter(log => {
    if (logFilter === 'paid') return isPaid(log);
    if (logFilter === 'unpaid') return !isPaid(log);
    return true;
  });

  const handleSendRelance = async (log: WooOrderLog) => {
    if (!log.customerEmail || !log.paymentUrl) {
      toast.error('Email ou lien panier manquant');
      return;
    }
    const toastId = toast.loading('Envoi de l\'email de relance...');
    try {
      await sendCartEmail(
        log.customerEmail,
        log.customerName || '',
        log.paymentUrl,
        log.orderTotal || '',
        log.clientNumber || ''
      );
      toast.success(`Email de relance envoyé à ${log.customerEmail}`, { id: toastId, duration: 4000 });
    } catch (e: any) {
      toast.error(`Erreur envoi email: ${e.message}`, { id: toastId });
    }
  };

  const copyReminderMessage = (log: WooOrderLog) => {
    const link = log.paymentUrl || 'https://www.lotismode.com/valider-un-panier-live/';
    const message = `Cher client(e),

Votre panier est prêt sur notre site internet. Vous y trouverez des articles sélectionnés spécialement pour vous !

Numéro de panier : ${log.clientNumber || ''}
Montant : ${log.orderTotal || '0'} €
Pour finaliser votre achat, veuillez cliquer sur le lien suivant : ${link}

Cordialement,
L'équipe de Lotis`;

    navigator.clipboard.writeText(message).then(() => {
      toast.success('Message copié !', { duration: 2000 });
    }).catch(() => {
      toast.error('Erreur lors de la copie');
    });
  };

  const getOrderStatusBadge = (log: WooOrderLog) => {
    if (log.status === 'error') return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 flex items-center gap-1"><MailX className="w-3 h-3" /> Erreur</span>;
    if (!log.orderId) return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</span>;
    
    const status = log.orderStatus;
    switch (status) {
      case 'processing':
      case 'completed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Payé</span>;
      case 'on-hold':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</span>;
      case 'cancelled':
      case 'refunded':
      case 'failed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 flex items-center gap-1"><MailX className="w-3 h-3" /> {status === 'cancelled' ? 'Annulé' : status === 'refunded' ? 'Remboursé' : 'Échoué'}</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Pas payé</span>;
    }
  };

  const handleRefresh = () => {
    loadData();
    toast.success('Actualisation des données...');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR');
  };

  const formatJsonField = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      return Array.isArray(parsed) ? parsed.join(', ') : jsonString;
    } catch {
      return jsonString;
    }
  };

  const getSuggestionForError = () => {
    if (!navigator.onLine) {
      return "Vous êtes hors ligne. Vérifiez votre connexion internet et réessayez.";
    }
    
    if (error?.includes('NetworkError') || error?.includes('fetch') || error?.includes('connexion')) {
      return (
        <div className="space-y-2">
          <p>Problèmes de connexion réseau. Vérifiez:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Que l'URL Supabase est correcte: <code className="bg-gray-100 px-1 py-0.5 rounded">{dbInfo.url}</code></li>
            <li>Que vous avez accès à Internet</li>
            <li>Que le serveur Supabase est accessible</li>
            <li>Qu'il n'y a pas de blocage CORS</li>
          </ul>
          <p className="italic text-sm">Note: Pour une utilisation hors-ligne, activez le mode démo depuis la page d'accueil.</p>
        </div>
      );
    }
    
    if (error?.includes('token') || error?.includes('authentification') || error?.includes('clé API')) {
      return (
        <div className="space-y-2">
          <p>Problème d'authentification. Vérifiez:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Que votre clé anonyme Supabase est correcte</li>
            <li>Que votre token d'authentification n'a pas expiré</li>
            <li>Que vous avez les permissions nécessaires</li>
          </ul>
        </div>
      );
    }
    
    if (error?.includes('table') || error?.includes('introuvable')) {
      return (
        <div className="space-y-2">
          <p>Problème de configuration de la base de données. Vérifiez:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Que la table <code className="bg-gray-100 px-1 py-0.5 rounded">{dbInfo.table}</code> existe dans votre base de données</li>
            <li>Que le schéma de la table est correct</li>
            <li>Que vous avez les droits d'accès à cette table</li>
          </ul>
        </div>
      );
    }
    
    return "Essayez de rafraîchir la page ou d'activer le mode hors-ligne sur la page d'accueil.";
  };

  const formatLogDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('diagnostic')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'diagnostic'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Info className="w-4 h-4" />
            Diagnostic
          </button>
          <button
            onClick={() => setActiveTab('log')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'log'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            Log WooCommerce
            {orderLogs.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">{orderLogs.length}</span>
            )}
          </button>
        </nav>
      </div>

      {/* Onglet Log WooCommerce */}
      {activeTab === 'log' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-purple-600" />
              Suivi des paniers
            </h2>
            <div className="flex gap-2">
              <button
                onClick={handleImportOrders}
                className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center text-sm"
                disabled={isLoadingLogs}
              >
                <ClipboardList className="w-4 h-4 mr-1" />
                Importer
              </button>
              <button
                onClick={() => { refreshLogs(); toast.success('Statuts mis à jour'); }}
                className="p-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 flex items-center text-sm"
                disabled={isLoadingLogs}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingLogs ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
              {orderLogs.length > 0 && (
                <button
                  onClick={handleClearLogs}
                  className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Effacer
                </button>
              )}
            </div>
          </div>

          {/* Stats rapides */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="border rounded-lg p-3 bg-green-50">
              <div className="text-2xl font-bold text-green-700">{orderLogs.filter(l => isPaid(l)).length}</div>
              <div className="text-sm text-green-600">Payés</div>
            </div>
            <div className="border rounded-lg p-3 bg-yellow-50">
              <div className="text-2xl font-bold text-yellow-700">{orderLogs.filter(l => !isPaid(l) && l.orderId !== null).length}</div>
              <div className="text-sm text-yellow-600">Pas payés</div>
            </div>
            <div className="border rounded-lg p-3 bg-red-50">
              <div className="text-2xl font-bold text-red-700">{orderLogs.filter(l => l.status === 'error').length}</div>
              <div className="text-sm text-red-600">Erreurs</div>
            </div>
            <div className="border rounded-lg p-3 bg-purple-50">
              <div className="text-2xl font-bold text-purple-700">{orderLogs.length}</div>
              <div className="text-sm text-purple-600">Total paniers</div>
            </div>
          </div>

          {/* Filtres */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setLogFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-full flex items-center gap-1 ${
                logFilter === 'all' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-3 h-3" /> Tous ({orderLogs.length})
            </button>
            <button
              onClick={() => setLogFilter('paid')}
              className={`px-3 py-1.5 text-sm rounded-full flex items-center gap-1 ${
                logFilter === 'paid' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <CheckCircle className="w-3 h-3" /> Payé ({orderLogs.filter(l => isPaid(l)).length})
            </button>
            <button
              onClick={() => setLogFilter('unpaid')}
              className={`px-3 py-1.5 text-sm rounded-full flex items-center gap-1 ${
                logFilter === 'unpaid' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <CreditCard className="w-3 h-3" /> Pas payé ({orderLogs.filter(l => !isPaid(l) && l.orderId !== null).length})
            </button>
          </div>

          {filteredLogs.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">{orderLogs.length === 0 ? 'Aucun panier envoyé' : 'Aucun résultat pour ce filtre'}</p>
              <p className="text-sm text-gray-400 mt-1">Les paniers apparaissent quand vous validez des produits</p>
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Panier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLogs.map((log, idx) => (
                    <tr key={idx} className={isPaid(log) ? 'bg-green-50/50' : log.status === 'error' ? 'bg-red-50' : ''}>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">{formatLogDate(log.timestamp)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{log.clientNumber}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{log.customerName || <span className="text-gray-400 italic">-</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.customerEmail ? (
                          <span className="text-blue-600">{log.customerEmail}</span>
                        ) : (
                          <span className="text-gray-400 italic">Aucun email</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {log.orderId ? `#${log.orderId}` : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {log.orderTotal ? `${log.orderTotal} €` : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{getOrderStatusBadge(log)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {isPaid(log) && log.orderId ? (
                            <a
                              href={`${getWooApiUrl()}/wp-admin/post.php?post=${log.orderId}&action=edit`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 bg-green-100 text-green-800 hover:bg-green-200 rounded text-xs flex items-center gap-1"
                              title="Voir la commande dans WooCommerce"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Commande
                            </a>
                          ) : (
                            <>
                              {log.paymentUrl && (
                                <a
                                  href={log.paymentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:text-purple-800 flex items-center gap-1 text-xs"
                                  title="Voir le panier"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                              {log.customerEmail && log.paymentUrl && (
                                <button
                                  onClick={() => handleSendRelance(log)}
                                  className="px-2 py-1 bg-orange-100 text-orange-800 hover:bg-orange-200 rounded text-xs flex items-center gap-1"
                                  title="Renvoyer l'email avec le lien panier"
                                >
                                  <MailX className="w-3 h-3" />
                                  Relance
                                </button>
                              )}
                              <button
                                onClick={() => copyReminderMessage(log)}
                                className="px-2 py-1 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded text-xs flex items-center gap-1"
                                title="Copier le message de relance"
                              >
                                <Copy className="w-3 h-3" />
                                Message
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Détail erreurs */}
          {orderLogs.some(l => l.errorMessage && l.status === 'error') && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-medium text-red-800 mb-2">Erreurs récentes</h4>
              {orderLogs.filter(l => l.status === 'error').map((log, idx) => (
                <div key={idx} className="text-sm text-red-700 mb-1">
                  <strong>{log.clientNumber}</strong>: {log.errorMessage}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Onglet Diagnostic */}
      {activeTab === 'diagnostic' && (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Diagnostic de la Base de Données</h2>
          <button
            onClick={handleRefresh}
            className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
            disabled={isLoading}
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-2">Configuration</h3>
            <div className="space-y-2">
              <div>
                <span className="font-medium">URL Supabase:</span>{' '}
                <span className="text-gray-600">{dbInfo.url}</span>
              </div>
              <div>
                <span className="font-medium">Table:</span>{' '}
                <span className="text-gray-600">{dbInfo.table}</span>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-2">Statut de connexion</h3>
            <div className="flex items-center">
              {connectionStatus === 'connecting' && (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-3"></div>
                  <span>Connexion en cours...</span>
                </>
              )}
              {connectionStatus === 'connected' && (
                <>
                  <div className="h-4 w-4 rounded-full bg-green-500 mr-3"></div>
                  <span className="text-green-700">Connecté à Supabase</span>
                </>
              )}
              {connectionStatus === 'failed' && (
                <>
                  <div className="h-4 w-4 rounded-full bg-red-500 mr-3"></div>
                  <span className="text-red-700">Connexion échouée</span>
                </>
              )}
            </div>
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="text-sm text-red-700">{error}</p>
                    
                    {/* Afficher les détails techniques en mode développement */}
                    {errorDetails && (
                      <details className="text-xs text-red-600 bg-red-50 p-2 rounded">
                        <summary className="cursor-pointer font-medium">Détails techniques</summary>
                        <pre className="mt-2 whitespace-pre-wrap overflow-auto">
                          {JSON.stringify({
                            message: errorDetails.message,
                            code: errorDetails.code,
                            details: errorDetails.details,
                            hint: errorDetails.hint
                          }, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section d'aide et suggestions */}
        {connectionStatus === 'failed' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <HelpCircle className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-800 mb-1">Suggestions pour résoudre le problème</h4>
                <div className="text-sm text-blue-700">
                  {getSuggestionForError()}
                </div>
                
                <div className="mt-4 p-3 bg-white border border-blue-100 rounded-md">
                  <p className="text-sm font-medium text-blue-800 mb-2">Actions possibles:</p>
                  <div className="flex flex-wrap gap-2">
                    <button 
                      onClick={handleRefresh}
                      className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 flex items-center"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" /> Réessayer
                    </button>
                    <button 
                      onClick={() => {
                        // Rediriger vers la page d'accueil pour activer le mode démo
                        window.location.href = '/';
                      }}
                      className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 flex items-center"
                    >
                      <WifiOff className="w-3 h-3 mr-1" /> Activer le mode hors-ligne
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold mb-4">Données ({data.length} enregistrements)</h3>
          
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-2" />
              <p className="text-red-700">Impossible de charger les données</p>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Info className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Aucune donnée trouvée dans la table</p>
              <p className="text-sm text-gray-400 mt-1">La table est vide ou inaccessible</p>
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Articles</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map(item => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.client_number}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatJsonField(item.items)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.total_price}€</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${item.status === 'draft' ? 'bg-blue-100 text-blue-800' : 
                            item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-green-100 text-green-800'}`}>
                          {item.status === 'draft' ? 'Brouillon' : 
                           item.status === 'pending' ? 'En cours' : 'Terminé'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(item.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}